import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { REALTIME_URL } from 'constants/api';
import { getViewerToken } from 'services/client/token';
import useViewer from 'hooks/useViewer';
import type {
  Feed,
  FeedClass,
  RtDelta,
  RtHello,
  RtPrices,
  RtSessionReset,
  Tier,
} from 'constants/payloads/realtime';
import { RealtimeClient, type RealtimeStatus } from './RealtimeClient';
import { shouldExpectRealtimeAuth, shouldReconnectRealtimeOnAuthChange } from './access';

export interface UseRealtimeFeedOptions {
  feed: Feed;
  /** flag && REALTIME_URL && page-specific condition. */
  enabled: boolean;
  params?: { tickers?: string[]; symbols?: string[]; feedClass?: FeedClass };
  onSnapshot: (body: unknown, hello: RtHello | null) => void;
  onDelta?: (delta: RtDelta) => void;
  onReset?: (reset: RtSessionReset) => void;
  onPrices?: (p: RtPrices) => void;
}

export interface UseRealtimeFeedResult {
  status: RealtimeStatus;
  tier: Tier | null;
  /** Send a client message (prices sub/unsub only). Buffered no-op when disconnected. */
  send: (msg: unknown) => void;
}

/**
 * React binding for RealtimeClient (spec §11.2). Reconnects when the feed,
 * enablement, or params change; reports connection status + entitlement tier.
 * When disabled (flag off, no REALTIME_URL, or fallback), status is 'off' /
 * 'fallback' and the surface keeps its legacy polling path.
 */
// Re-arm one connect attempt just past the RealtimeClient's 10-min fallback cooldown, so
// a surface that fell back on two transient blips heals after the cooldown instead of
// staying on polling until the next navigation/param change (§17.10 C14).
const FALLBACK_RETRY_MS = 11 * 60_000;

export function useRealtimeFeed(opts: UseRealtimeFeedOptions): UseRealtimeFeedResult {
  const { feed, enabled, params } = opts;
  const [status, setStatus] = useState<RealtimeStatus>('off');
  const [tier, setTier] = useState<Tier | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const clientRef = useRef<RealtimeClient | null>(null);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep callbacks in a ref so changing closures don't force a reconnect.
  const cbRef = useRef(opts);
  cbRef.current = opts;

  // Reconnect auth-gated feeds on login/logout: their tier/token are fixed at
  // connect. Prices is public, so auth resolving must not churn the base socket.
  const viewer = useViewer();
  const viewerId = viewer?.viewer?.uid ?? '';
  const authKey = shouldReconnectRealtimeOnAuthChange(feed) ? viewerId : '';

  // Stable dependency key for params so we only reconnect on real changes.
  const paramsKey = useMemo(
    () =>
      JSON.stringify({
        tickers: params?.tickers ? [...params.tickers].sort() : undefined,
        symbols: params?.symbols ? [...params.symbols].sort() : undefined,
        feedClass: params?.feedClass,
      }),
    [params?.tickers, params?.symbols, params?.feedClass],
  );

  useEffect(() => {
    if (!enabled || !REALTIME_URL) {
      setStatus('off');
      setTier(null);
      return;
    }

    const client = new RealtimeClient({
      feed,
      baseUrl: REALTIME_URL,
      params: JSON.parse(paramsKey),
      getToken: getViewerToken,
      // A logged-in viewer must never fall back to an anonymous socket on auth-gated feeds
      // (paid user → silent free downgrade). Prices is public, so token failure cannot block it.
      expectAuth: shouldExpectRealtimeAuth(feed, viewerId, params?.feedClass),
      onSnapshot: (body, hello) => cbRef.current.onSnapshot(body, hello),
      onDelta: (delta) => cbRef.current.onDelta?.(delta),
      onReset: (reset) => cbRef.current.onReset?.(reset),
      onPrices: (p) => cbRef.current.onPrices?.(p),
      onStatus: (s) => {
        setStatus(s);
        // A terminal fallback client never reconnects on its own; schedule ONE recreate
        // past the cooldown so realtime heals rather than polling for the tab's life (§17.10 C14).
        // EXCEPT a capability-gap fallback (no gzip) — it can never succeed, so don't recreate
        // a client every 11 min for the tab's life; polling owns it permanently (§17.11.2).
        if (s === 'fallback' && !client.isPermanentFallback()) {
          if (retryTimer.current) clearTimeout(retryTimer.current);
          retryTimer.current = setTimeout(() => {
            retryTimer.current = null;
            setRetryToken((t) => t + 1);
          }, FALLBACK_RETRY_MS);
        }
      },
      onTier: setTier,
    });
    clientRef.current = client;
    client.start();

    return () => {
      if (retryTimer.current) clearTimeout(retryTimer.current);
      client.stop();
      clientRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feed, enabled, paramsKey, authKey, retryToken]);

  const send = useCallback((msg: unknown) => clientRef.current?.send(msg), []);
  return { status, tier, send };
}
